import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useGlobalStore } from '../../application/store/store.tsx';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import AvailableStorages from './components/AvailableStorages.tsx';
import { Box, Button, Step, StepLabel, Stepper } from '@mui/material';
import Sign from './components/Sign.tsx';
import DrugInventoryManagement from './components/DrugInventoryManagement.tsx';
import {
    TransactionOverview,
    TransactionType,
} from '../../application/types/transactionType.ts';
import SignedSuccess from './components/SignedSuccess.tsx';
import { StorageType } from '../../application/types/storageType.ts';
import Visibility from '../../common/components/Visibility.tsx';
import { getTransactionAlert } from '../../common/utils/transactionValidation.tsx';
import Note from './components/Note.tsx';

const steps = ['Lagerställe', 'Läkemedel'];

const Inventory = () => {
    const resetStorage = useGlobalStore((state) => state.resetAvailableStorage);
    const resetSelectedDrug = useGlobalStore(
        (state) => state.resetSelectedDrug
    );
    const resetNote = useGlobalStore((state) => state.resetNote);
    const resetInventoryRecords = useGlobalStore(
        (state) => state.resetInventoryRecords
    );
    const resetInventoryList = useGlobalStore(
        (state) => state.resetInventoryList
    );
    const { setDoubleSignature, showSigningInInventory } = useGlobalStore();

    const inventoryRecordOverview = useGlobalStore(
        (state) => state.inventoryRecordOverview
    );
    const selectedStorage = useGlobalStore((state) => state.selectedStorage);
    const setSelectedStorage = useGlobalStore(
        (state) => state.setSelectedStorage
    );

    const stepContents = [`${selectedStorage?.name ?? ''}`, ''];

    const [activeStep, setActiveStep] = useState(0);
    const [searchTermStorage, setSearchTermStorage] = useState<string>('');

    const navigate = useNavigate();

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleNavigateBack = () => {
        // Clear all state before navigating
        resetSelectedDrug();
        resetStorage();
        resetNote();
        resetInventoryRecords();
        resetInventoryList();
        setDoubleSignature(false);

        // Reset overview state
        useGlobalStore.getState().resetOverviews();

        navigate(NavigationRoutes.transaction);
    };
    const handleSelectStorage = (storage) => {
        const formattedStorageValue = `${selectedStorage?.name ?? ''}`;
        setSelectedStorage(storage);
        setSearchTermStorage(formattedStorageValue);
    };
    useEffect(() => {
        return () => {
            resetSelectedDrug();
            resetStorage();
            setDoubleSignature(false);
            resetNote();
            resetInventoryRecords();
            resetInventoryList();
        };
    }, [resetSelectedDrug, resetStorage, setDoubleSignature, resetNote]);
    const [isStepperOpen, setIsStepperOpen] = useState(false);
    const toggleStepper = () => {
        setIsStepperOpen(!isStepperOpen);
    };
    return (
        <div>
            <div className="flex flex-row justify-between items-center mb-4 text-xs sm:text-base">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleNavigateBack}
                    color="primary"
                    className="!text-xs sm:!text-base"
                    size="small"
                    sx={{
                        '@media (min-width: 640px)': {
                            fontSize: 'inherit',
                            padding: '6px 16px',
                        },
                    }}
                >
                    Tillbaka
                </Button>
            </div>
            {(inventoryRecordOverview === TransactionOverview.formOverview ||
                inventoryRecordOverview ===
                    TransactionOverview.doubleSignOverview) && (
                <div className="flex min-h-screen bg-gray-50 relative">
                    {/* Stepper Toggle Button - Only visible below 1024px */}
                    <div className="hidden max-lg:block">
                        <button
                            onClick={toggleStepper}
                            className={`fixed top-1/2 -translate-y-1/2 z-50 bg-gray-200 p-2 rounded-r-md shadow-md transition-transform duration-300 ${
                                isStepperOpen
                                    ? 'translate-x-64'
                                    : 'translate-x-0'
                            }`}
                            style={{ left: 0 }}
                        >
                            {isStepperOpen ? (
                                <ChevronLeftIcon />
                            ) : (
                                <ChevronRightIcon />
                            )}
                        </button>
                    </div>

                    {/* Stepper Container */}
                    <div
                        className={`
                            lg:w-1/5 lg:block lg:relative lg:translate-x-0
                            max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:h-full max-lg:w-64 max-lg:z-40
                            transition-transform duration-300
                            p-5 shadow-xl rounded-lg bg-white
                            ${!isStepperOpen && 'max-lg:-translate-x-full'}
                        `}
                    >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={index}>
                                    <StepLabel>
                                        <div className="flex flex-col">
                                            <span className="text-lg font-semibold text-gray-900 text-left">
                                                {label}
                                            </span>
                                            {stepContents[index] ? (
                                                <span className="text-sm text-gray-600 italic text-left">
                                                    {stepContents[index]}
                                                </span>
                                            ) : null}
                                        </div>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>

                    {/* Overlay - Only visible below 1024px when stepper is open */}
                    {isStepperOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-30 hidden max-lg:block"
                            onClick={toggleStepper}
                        />
                    )}

                    {/* Main Content */}
                    <div className="w-full lg:w-4/5 p-2 sm:p-5 shadow-xl rounded-lg lg:ml-5 mx-2 sm:mx-5 overflow-auto">
                        <Visibility
                            visible={
                                inventoryRecordOverview !==
                                TransactionOverview.doubleSignOverview
                            }
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={4}
                            >
                                <Button
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    variant="text"
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        padding: {
                                            xs: '4px 8px',
                                            sm: '6px 16px',
                                        },
                                    }}
                                >
                                    Tillbaka
                                </Button>
                                <h1 className="text-center text-lg sm:text-2xl">
                                    Registrera inventering
                                </h1>
                                <Button
                                    onClick={handleNext}
                                    disabled={activeStep === steps.length - 1}
                                    variant="text"
                                    endIcon={
                                        activeStep !== steps.length - 1 ? (
                                            <ArrowForwardIcon />
                                        ) : (
                                            ''
                                        )
                                    }
                                    sx={{
                                        padding: {
                                            xs: '4px 8px',
                                            sm: '6px 16px',
                                        },
                                    }}
                                >
                                    {activeStep === steps.length - 1
                                        ? ''
                                        : 'Nästa'}
                                </Button>
                            </Box>
                        </Visibility>
                        {inventoryRecordOverview ===
                            TransactionOverview.doubleSignOverview && (
                            <Box mb={4}>
                                <h1 className="text-center text-lg sm:text-2xl">
                                    Dubbelsignera inventering
                                </h1>
                            </Box>
                        )}

                        {/* Step Contents */}
                        {activeStep === 0 &&
                            inventoryRecordOverview !==
                                TransactionOverview.doubleSignOverview && (
                                <div className="flex flex-col items-center">
                                    <Box
                                        sx={{
                                            width: {
                                                sm: '20rem',
                                                xs: '20rem',
                                                lg: '24rem',
                                            },
                                            maxWidth: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <AvailableStorages
                                            label="Lagerställe"
                                            searchTerm={searchTermStorage}
                                            setSearchTerm={setSearchTermStorage}
                                            storageType={StorageType.storage}
                                            transactionType={
                                                TransactionType.Crosscheck
                                            }
                                            handleSelect={handleSelectStorage}
                                            handleSelectEmergency={function ():
                                                | void
                                                | undefined {
                                                throw new Error(
                                                    'Function not implemented.'
                                                );
                                            }}
                                            className="w-full"
                                        />
                                    </Box>
                                </div>
                            )}
                        {activeStep === 1 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: {
                                            sm: '20rem',
                                            xs: '20rem',
                                            lg: '24rem',
                                        },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    {getTransactionAlert(
                                        TransactionType.Crosscheck,
                                        undefined,
                                        selectedStorage,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        width: {
                                            sm: '20rem',
                                            xs: '20rem',
                                            lg: '24rem',
                                        },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    <DrugInventoryManagement />
                                </Box>
                                <Box
                                    sx={{
                                        width: {
                                            sm: '20rem',
                                            xs: '20rem',
                                            lg: '24rem',
                                        },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                        mb: 0,
                                    }}
                                >
                                    <Note label="Anteckning" />
                                </Box>
                                <Visibility visible={showSigningInInventory}>
                                    <div className="w-full px-2 sm:px-4 max-w-[calc(100vw-16px)] sm:max-w-md mx-auto my-2 sm:my-4">
                                        <Sign
                                            transactionType={
                                                TransactionType.Crosscheck
                                            }
                                        />
                                    </div>
                                </Visibility>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {inventoryRecordOverview ===
                TransactionOverview.successOverview && (
                <SignedSuccess
                    transactionFormOverview={TransactionType.Crosscheck}
                />
            )}
        </div>
    );
};

export default Inventory;
